import { AbilityId, ClassType, ResourceType, TargetType, DRCategory, BuffType, } from '../types/index.js';
// === Game Config ===
export const TICK_RATE = 20; // Hz
export const TICK_MS = 1000 / TICK_RATE; // 50ms
export const ARENA_SIZE = 40; // half-size, total 80x80
export const ARENA_WALL_HEIGHT = 5;
export const MOVE_SPEED = 7; // units per second
export const GCD_MS = 1500; // global cooldown
export const COUNTDOWN_SECONDS = 5;
export const MATCH_TIME_LIMIT_MS = 5 * 60 * 1000; // 5 min
// === Starting Positions ===
export const SPAWN_POSITIONS_1V1 = [
    { x: -15, y: 0, z: 0 },
    { x: 15, y: 0, z: 0 },
];
export const SPAWN_POSITIONS_2V2 = [
    { x: -15, y: 0, z: -5 },
    { x: -15, y: 0, z: 5 },
    { x: 15, y: 0, z: -5 },
    { x: 15, y: 0, z: 5 },
];
// === Arena Pillars ===
export const ARENA_PILLARS = [
    { position: { x: -10, y: 0, z: -10 }, radius: 2, height: 6 },
    { position: { x: 10, y: 0, z: -10 }, radius: 2, height: 6 },
    { position: { x: -10, y: 0, z: 10 }, radius: 2, height: 6 },
    { position: { x: 10, y: 0, z: 10 }, radius: 2, height: 6 },
];
// === Class Stats ===
export const CLASS_STATS = {
    [ClassType.Warrior]: {
        maxHp: 5000,
        maxResource: 100,
        resourceType: ResourceType.Rage,
        resourceRegen: 0, // no passive decay to keep the opener readable
        armor: 0.3, // 30% physical damage reduction
        moveSpeed: MOVE_SPEED,
    },
    [ClassType.Mage]: {
        maxHp: 3500,
        maxResource: 3000,
        resourceType: ResourceType.Mana,
        resourceRegen: 30, // mana per second
        armor: 0.05,
        moveSpeed: MOVE_SPEED,
    },
    [ClassType.Priest]: {
        maxHp: 3800,
        maxResource: 4000,
        resourceType: ResourceType.Mana,
        resourceRegen: 35,
        armor: 0.05,
        moveSpeed: MOVE_SPEED,
    },
    [ClassType.Paladin]: {
        maxHp: 4300,
        maxResource: 3500,
        resourceType: ResourceType.Mana,
        resourceRegen: 28,
        armor: 0.20,
        moveSpeed: MOVE_SPEED,
    },
};
export const AUTO_ATTACKS = {
    [ClassType.Warrior]: {
        damage: 170,
        intervalMs: 1800,
        range: 5,
        type: 'melee',
    },
    [ClassType.Mage]: {
        damage: 125,
        intervalMs: 2200,
        range: 30,
        type: 'ranged',
    },
    [ClassType.Priest]: {
        damage: 115,
        intervalMs: 2300,
        range: 30,
        type: 'ranged',
    },
    [ClassType.Paladin]: {
        damage: 190,
        intervalMs: 1700,
        range: 5,
        type: 'melee',
    },
};
// === Ability Definitions ===
export const ABILITIES = {
    // --- Warrior ---
    [AbilityId.Charge]: {
        id: AbilityId.Charge,
        name: 'Charge',
        classType: ClassType.Warrior,
        targetType: TargetType.Enemy,
        castTime: 0,
        cooldown: 12,
        resourceCost: 0,
        range: 25,
        damage: 100,
        healing: 0,
        duration: 1000, // root 1s
        description: 'Charge enemy, root 1s. Min 8yd.',
    },
    [AbilityId.MortalStrike]: {
        id: AbilityId.MortalStrike,
        name: 'Mortal Strike',
        classType: ClassType.Warrior,
        targetType: TargetType.Enemy,
        castTime: 0,
        cooldown: 6,
        resourceCost: 30,
        range: 5,
        damage: 600,
        healing: 0,
        duration: 6000, // healing debuff 6s
        description: 'Heavy strike, -50% healing 6s.',
    },
    [AbilityId.ShieldBlock]: {
        id: AbilityId.ShieldBlock,
        name: 'Shield Block',
        classType: ClassType.Warrior,
        targetType: TargetType.Self,
        castTime: 0,
        cooldown: 8,
        resourceCost: 20,
        range: 0,
        damage: 0,
        healing: 0,
        duration: 4000,
        description: '+50% armor for 4s.',
    },
    [AbilityId.Execute]: {
        id: AbilityId.Execute,
        name: 'Execute',
        classType: ClassType.Warrior,
        targetType: TargetType.Enemy,
        castTime: 0,
        cooldown: 10,
        resourceCost: 40,
        range: 5,
        damage: 900,
        healing: 0,
        duration: 0,
        description: 'High damage. Only below 20% HP.',
    },
    [AbilityId.Hamstring]: {
        id: AbilityId.Hamstring,
        name: 'Hamstring',
        classType: ClassType.Warrior,
        targetType: TargetType.Enemy,
        castTime: 0,
        cooldown: 0,
        resourceCost: 10,
        range: 5,
        damage: 100,
        healing: 0,
        duration: 6000,
        description: '50% slow for 6s.',
    },
    // --- Mage ---
    [AbilityId.Fireball]: {
        id: AbilityId.Fireball,
        name: 'Fireball',
        classType: ClassType.Mage,
        targetType: TargetType.Enemy,
        castTime: 1.5,
        cooldown: 0,
        resourceCost: 200,
        range: 30,
        damage: 700,
        healing: 0,
        duration: 0,
        description: '1.5s cast, high damage.',
    },
    [AbilityId.FrostNova]: {
        id: AbilityId.FrostNova,
        name: 'Frost Nova',
        classType: ClassType.Mage,
        targetType: TargetType.Self,
        castTime: 0,
        cooldown: 20,
        resourceCost: 150,
        range: 8, // AoE around self
        damage: 50,
        healing: 0,
        duration: 4000,
        description: 'Root enemies within 8yd for 4s.',
    },
    [AbilityId.Blink]: {
        id: AbilityId.Blink,
        name: 'Blink',
        classType: ClassType.Mage,
        targetType: TargetType.Self,
        castTime: 0,
        cooldown: 15,
        resourceCost: 100,
        range: 15,
        damage: 0,
        healing: 0,
        duration: 0,
        description: 'Teleport 15yd forward.',
    },
    [AbilityId.Polymorph]: {
        id: AbilityId.Polymorph,
        name: 'Polymorph',
        classType: ClassType.Mage,
        targetType: TargetType.Enemy,
        castTime: 1.5,
        cooldown: 0,
        resourceCost: 200,
        range: 30,
        damage: 0,
        healing: 0,
        duration: 6000,
        description: '1.5s cast, CC 6s. Breaks on damage.',
    },
    [AbilityId.IceBarrier]: {
        id: AbilityId.IceBarrier,
        name: 'Ice Barrier',
        classType: ClassType.Mage,
        targetType: TargetType.Self,
        castTime: 0,
        cooldown: 30,
        resourceCost: 300,
        range: 0,
        damage: 0,
        healing: 0,
        duration: 30000,
        description: 'Absorb shield (1000 damage).',
    },
    [AbilityId.Counterspell]: {
        id: AbilityId.Counterspell,
        name: 'Counterspell',
        classType: ClassType.Mage,
        targetType: TargetType.Enemy,
        castTime: 0,
        cooldown: 24,
        resourceCost: 150,
        range: 30,
        damage: 0,
        healing: 0,
        duration: 6000,
        description: 'Interrupt enemy cast. Silence 6s.',
    },
    // --- Paladin ---
    [AbilityId.CrusaderStrike]: {
        id: AbilityId.CrusaderStrike,
        name: 'Crusader Strike',
        classType: ClassType.Paladin,
        targetType: TargetType.Enemy,
        castTime: 0,
        cooldown: 6,
        resourceCost: 40,
        range: 5,
        damage: 600,
        healing: 0,
        duration: 6000,
        description: 'Melee strike, 600 dmg. -50% healing 6s.',
    },
    [AbilityId.HammerOfJustice]: {
        id: AbilityId.HammerOfJustice,
        name: 'Hammer of Justice',
        classType: ClassType.Paladin,
        targetType: TargetType.Enemy,
        castTime: 0,
        cooldown: 40,
        resourceCost: 100,
        range: 10,
        damage: 0,
        healing: 0,
        duration: 3000,
        description: 'Stun target 3s. 10yd range.',
    },
    [AbilityId.FlashOfLight]: {
        id: AbilityId.FlashOfLight,
        name: 'Flash of Light',
        classType: ClassType.Paladin,
        targetType: TargetType.Friendly,
        castTime: 1.5,
        cooldown: 6,
        resourceCost: 300,
        range: 30,
        damage: 0,
        healing: 900,
        duration: 0,
        description: '1.5s cast, 900 heal.',
    },
    [AbilityId.DivineShield]: {
        id: AbilityId.DivineShield,
        name: 'Divine Shield',
        classType: ClassType.Paladin,
        targetType: TargetType.Self,
        castTime: 0,
        cooldown: 120,
        resourceCost: 0,
        range: 0,
        damage: 0,
        healing: 0,
        duration: 3000,
        description: 'Immune to damage 3s. Cannot attack.',
    },
    [AbilityId.Consecration]: {
        id: AbilityId.Consecration,
        name: 'Consecration',
        classType: ClassType.Paladin,
        targetType: TargetType.Self,
        castTime: 0,
        cooldown: 8,
        resourceCost: 200,
        range: 8,
        damage: 350,
        healing: 0,
        duration: 0,
        description: 'AoE holy dmg 350 within 8yd.',
    },
    // --- Priest ---
    [AbilityId.Heal]: {
        id: AbilityId.Heal,
        name: 'Heal',
        classType: ClassType.Priest,
        targetType: TargetType.Friendly,
        castTime: 2,
        cooldown: 0,
        resourceCost: 350,
        range: 30,
        damage: 0,
        healing: 900,
        duration: 0,
        description: '2s cast, large heal.',
    },
    [AbilityId.ShadowWordPain]: {
        id: AbilityId.ShadowWordPain,
        name: 'Shadow Word: Pain',
        classType: ClassType.Priest,
        targetType: TargetType.Enemy,
        castTime: 0,
        cooldown: 0,
        resourceCost: 200,
        range: 30,
        damage: 600, // total DoT damage
        healing: 0,
        duration: 12000,
        description: 'DoT, 600 damage over 12s.',
    },
    [AbilityId.PowerWordShield]: {
        id: AbilityId.PowerWordShield,
        name: 'Power Word: Shield',
        classType: ClassType.Priest,
        targetType: TargetType.Friendly,
        castTime: 0,
        cooldown: 12,
        resourceCost: 300,
        range: 30,
        damage: 0,
        healing: 0,
        duration: 15000,
        description: 'Absorb shield (800 damage).',
    },
    [AbilityId.Smite]: {
        id: AbilityId.Smite,
        name: 'Smite',
        classType: ClassType.Priest,
        targetType: TargetType.Enemy,
        castTime: 1.5,
        cooldown: 0,
        resourceCost: 180,
        range: 30,
        damage: 450,
        healing: 0,
        duration: 0,
        description: '1.5s cast, moderate damage.',
    },
    [AbilityId.PsychicScream]: {
        id: AbilityId.PsychicScream,
        name: 'Psychic Scream',
        classType: ClassType.Priest,
        targetType: TargetType.Self,
        castTime: 0,
        cooldown: 30,
        resourceCost: 250,
        range: 8,
        damage: 0,
        healing: 0,
        duration: 3000,
        description: 'AoE fear 3s within 8yd.',
    },
};
// Get abilities for a specific class
export function getClassAbilities(classType) {
    return Object.values(ABILITIES).filter(a => a.classType === classType);
}
// === Buff → DR Category mapping ===
export const BUFF_DR_CATEGORY = {
    [BuffType.Root]: DRCategory.Root,
    [BuffType.Polymorph]: DRCategory.Incapacitate,
    [BuffType.Fear]: DRCategory.Fear,
    [BuffType.Stun]: DRCategory.Stun,
};
// === DR Multipliers ===
export const DR_MULTIPLIERS = [1, 0.5, 0.25, 0]; // full, half, quarter, immune
export const DR_RESET_MS = 15000;
// === Elo ===
export const ELO_START = 1500;
export const ELO_K = 32;
export const RATING_BRACKETS = [
    { name: 'Bronze', min: 0, max: 1399 },
    { name: 'Silver', min: 1400, max: 1599 },
    { name: 'Gold', min: 1600, max: 1799 },
    { name: 'Platinum', min: 1800, max: 1999 },
    { name: 'Diamond', min: 2000, max: Infinity },
];
// === Currency ===
export const WIN_CURRENCY_BASE = 10;
export const WIN_CURRENCY_RATING_DIVISOR = 200;
export const LOSS_CURRENCY = 3;
// === Talents ===
export const TALENT_COSTS = [50, 150, 300];
export const TALENT_REFUND_RATE = 0.5;
export const TALENTS = {
    // Warrior
    warrior_damage: {
        id: 'warrior_damage',
        classType: ClassType.Warrior,
        name: 'Battle Fury',
        description: 'Increases damage dealt',
        maxRank: 3,
        costs: TALENT_COSTS,
        bonusPerRank: [3, 6, 10],
        stat: 'damage',
    },
    warrior_hp: {
        id: 'warrior_hp',
        classType: ClassType.Warrior,
        name: 'Toughness',
        description: 'Increases maximum health',
        maxRank: 3,
        costs: TALENT_COSTS,
        bonusPerRank: [3, 6, 10],
        stat: 'maxHp',
    },
    warrior_armor: {
        id: 'warrior_armor',
        classType: ClassType.Warrior,
        name: 'Thick Skin',
        description: 'Increases armor',
        maxRank: 3,
        costs: TALENT_COSTS,
        bonusPerRank: [3, 6, 10],
        stat: 'armor',
    },
    warrior_rage: {
        id: 'warrior_rage',
        classType: ClassType.Warrior,
        name: 'Rage Mastery',
        description: 'Increases rage generation',
        maxRank: 3,
        costs: TALENT_COSTS,
        bonusPerRank: [5, 10, 15],
        stat: 'rageGen',
    },
    warrior_crit: {
        id: 'warrior_crit',
        classType: ClassType.Warrior,
        name: 'Precision',
        description: 'Increases critical strike chance',
        maxRank: 3,
        costs: TALENT_COSTS,
        bonusPerRank: [3, 6, 10],
        stat: 'crit',
    },
    // Mage
    mage_damage: {
        id: 'mage_damage',
        classType: ClassType.Mage,
        name: 'Arcane Power',
        description: 'Increases spell damage',
        maxRank: 3,
        costs: TALENT_COSTS,
        bonusPerRank: [3, 6, 10],
        stat: 'damage',
    },
    mage_hp: {
        id: 'mage_hp',
        classType: ClassType.Mage,
        name: 'Arcane Fortitude',
        description: 'Increases maximum health',
        maxRank: 3,
        costs: TALENT_COSTS,
        bonusPerRank: [3, 6, 10],
        stat: 'maxHp',
    },
    mage_mana: {
        id: 'mage_mana',
        classType: ClassType.Mage,
        name: 'Mana Flow',
        description: 'Increases mana regeneration',
        maxRank: 3,
        costs: TALENT_COSTS,
        bonusPerRank: [5, 10, 15],
        stat: 'manaRegen',
    },
    mage_barrier: {
        id: 'mage_barrier',
        classType: ClassType.Mage,
        name: 'Shatter Proof',
        description: 'Increases Ice Barrier absorb',
        maxRank: 3,
        costs: TALENT_COSTS,
        bonusPerRank: [5, 10, 20],
        stat: 'barrierAbsorb',
    },
    mage_cast: {
        id: 'mage_cast',
        classType: ClassType.Mage,
        name: 'Quick Cast',
        description: 'Reduces cast time',
        maxRank: 3,
        costs: TALENT_COSTS,
        bonusPerRank: [3, 6, 10],
        stat: 'castSpeed',
    },
    // Priest
    priest_healing: {
        id: 'priest_healing',
        classType: ClassType.Priest,
        name: 'Divine Grace',
        description: 'Increases healing done',
        maxRank: 3,
        costs: TALENT_COSTS,
        bonusPerRank: [3, 6, 10],
        stat: 'healing',
    },
    priest_hp: {
        id: 'priest_hp',
        classType: ClassType.Priest,
        name: 'Inner Strength',
        description: 'Increases maximum health',
        maxRank: 3,
        costs: TALENT_COSTS,
        bonusPerRank: [3, 6, 10],
        stat: 'maxHp',
    },
    priest_damage: {
        id: 'priest_damage',
        classType: ClassType.Priest,
        name: 'Shadow Focus',
        description: 'Increases damage dealt',
        maxRank: 3,
        costs: TALENT_COSTS,
        bonusPerRank: [3, 6, 10],
        stat: 'damage',
    },
    priest_mana: {
        id: 'priest_mana',
        classType: ClassType.Priest,
        name: 'Meditation',
        description: 'Increases mana regeneration',
        maxRank: 3,
        costs: TALENT_COSTS,
        bonusPerRank: [5, 10, 15],
        stat: 'manaRegen',
    },
    priest_shield: {
        id: 'priest_shield',
        classType: ClassType.Priest,
        name: 'Improved Shields',
        description: 'Increases PW:Shield absorb',
        maxRank: 3,
        costs: TALENT_COSTS,
        bonusPerRank: [5, 10, 20],
        stat: 'shieldAbsorb',
    },
    // Paladin
    paladin_damage: {
        id: 'paladin_damage',
        classType: ClassType.Paladin,
        name: 'Retribution',
        description: 'Increases damage dealt',
        maxRank: 3,
        costs: TALENT_COSTS,
        bonusPerRank: [3, 6, 10],
        stat: 'damage',
    },
    paladin_hp: {
        id: 'paladin_hp',
        classType: ClassType.Paladin,
        name: 'Holy Fortitude',
        description: 'Increases maximum health',
        maxRank: 3,
        costs: TALENT_COSTS,
        bonusPerRank: [3, 6, 10],
        stat: 'maxHp',
    },
    paladin_healing: {
        id: 'paladin_healing',
        classType: ClassType.Paladin,
        name: 'Sacred Healing',
        description: 'Increases healing done',
        maxRank: 3,
        costs: TALENT_COSTS,
        bonusPerRank: [3, 6, 10],
        stat: 'healing',
    },
    paladin_mana: {
        id: 'paladin_mana',
        classType: ClassType.Paladin,
        name: 'Divine Wisdom',
        description: 'Increases mana regeneration',
        maxRank: 3,
        costs: TALENT_COSTS,
        bonusPerRank: [5, 10, 15],
        stat: 'manaRegen',
    },
    paladin_armor: {
        id: 'paladin_armor',
        classType: ClassType.Paladin,
        name: 'Plate Mastery',
        description: 'Increases armor',
        maxRank: 3,
        costs: TALENT_COSTS,
        bonusPerRank: [3, 6, 10],
        stat: 'armor',
    },
};
export function getClassTalents(classType) {
    return Object.values(TALENTS).filter(t => t.classType === classType);
}
//# sourceMappingURL=index.js.map